import { useState, useMemo, KeyboardEvent, useEffect, useRef } from 'react';
import { ArrowRight, ChevronsDownUp, ChevronsUpDown, Eye, EyeOff, Plus, X } from 'lucide-react';
import pluralize from 'pluralize';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Entity, ExtractionModel } from 'src/shared/types/extraction-model.types';
import { Entities, EntityValue, isEntityObject } from 'src/features/messages/types/message.types';
import { cn } from 'src/shared/lib/utils';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import TagNameBadge from 'src/shared/components/entities/components/TagNameBadge';
import CopyButton from 'src/shared/components/ui/icons/copy-button';
import { getRegexOptions } from 'src/shared/constants/regex-formats/_regex.index';
import { Calendar } from 'src/shared/components/ui/calendar';
import { Popover, PopoverTrigger } from 'src/shared/components/ui/popover';
import DynamicIcon, { Icons } from 'src/shared/components/ui/icons/dynamic-icon';

// ============================================================================
// Types
// ============================================================================

export type NextGenEntityEditorProps = {
  /** The extraction model definition containing entity schemas */
  model: ExtractionModel;
  /** The prediction values (JSON object with entity keys and values) */
  values: Entities[string];
  /** Whether the editor is in editable mode */
  editable?: boolean;
  /** Callback when a value is saved. Receives the entity key, new value, and full path */
  onSave?: (
    key: string,
    value: string | number | boolean | string[] | number[] | boolean[] | Entities[string][] | null,
    path: string[]
  ) => void;
};

type InternalTreeProps = NextGenEntityEditorProps & {
  depth?: number;
  path?: string[];
  parentEntity?: Entity;
  showMissingTags?: boolean;
};

type PrimitiveValueProps = {
  entity: Entity;
  value: string | number | boolean | string[] | number[] | boolean[] | null | undefined;
  path: string[];
  editable?: boolean;
  onSave?: (
    key: string,
    value: string | number | boolean | string[] | number[] | boolean[] | null,
    path: string[]
  ) => void;
};

type EntityType = 'PRIMITIVE' | 'ARRAY' | 'OBJECT';

// ============================================================================
// Utility Functions
// ============================================================================

/** Get friendly display name for a regex pattern */
function getRegexDisplayName(regexPattern: string): string | null {
  const allOptions = getRegexOptions();
  for (const [category, options] of Object.entries(allOptions)) {
    for (const opt of options) {
      if (opt.regex === regexPattern) {
        return `${category}: ${opt.display}`;
      }
    }
  }
  return null;
}

/** Validate input against entity's regex pattern (for strings) */
function validateStringInput(value: string, entity: Entity): { isValid: boolean; error: string | null } {
  if (!value.trim()) {
    return { isValid: true, error: null };
  }

  const regex = entity.outputFormat?.type === 'string' ? entity.outputFormat.regex : undefined;
  if (!regex) {
    return { isValid: true, error: null };
  }

  try {
    const re = new RegExp(regex);
    if (re.test(value)) {
      return { isValid: true, error: null };
    }
    const friendlyName = getRegexDisplayName(regex);
    return {
      isValid: false,
      error: friendlyName ? `Must match format: ${friendlyName}` : 'Invalid format',
    };
  } catch {
    return { isValid: true, error: null };
  }
}

/** Validate number input */
function validateNumberInput(
  value: string,
  entity: Entity
): { isValid: boolean; error: string | null; parsed?: number } {
  if (!value.trim()) {
    return { isValid: true, error: null };
  }

  const isInteger = entity.outputFormat?.type === 'integer';
  const parsed = isInteger ? parseInt(value, 10) : parseFloat(value);

  if (isNaN(parsed)) {
    return {
      isValid: false,
      error: isInteger ? 'Must be a valid integer' : 'Must be a valid number',
    };
  }

  if (isInteger && !Number.isInteger(parsed)) {
    return {
      isValid: false,
      error: 'Must be a valid integer',
    };
  }

  return { isValid: true, error: null, parsed };
}

/** Check if entity is a date format */
function isDateFormat(entity: Entity): boolean {
  const formatId = entity.outputFormat?.formatId;
  return typeof formatId === 'string' && formatId.startsWith('date-');
}

/** Parse date string according to format */
function parseDateString(dateString: string, formatId: string): Date | null {
  if (!dateString || !formatId) return null;

  try {
    // Handle different date formats
    if (formatId === 'date-iso') {
      // YYYY-MM-DD
      return new Date(dateString);
    } else if (formatId === 'date-eu-slash' || formatId === 'date-eu-dash' || formatId === 'date-eu-dot') {
      // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
      const parts = dateString.split(/[/\-.]/);
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    } else if (formatId === 'date-us-slash' || formatId === 'date-us-dash') {
      // MM/DD/YYYY, MM-DD-YYYY
      const parts = dateString.split(/[/-]/);
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }

    // Fallback: try parsing as-is
    const parsed = new Date(dateString);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

/** Format date according to formatId */
function formatDateString(date: Date, formatId: string): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (formatId === 'date-iso') {
    return `${year}-${month}-${day}`;
  } else if (formatId === 'date-eu-slash') {
    return `${day}/${month}/${year}`;
  } else if (formatId === 'date-eu-dash') {
    return `${day}-${month}-${year}`;
  } else if (formatId === 'date-eu-dot') {
    return `${day}.${month}.${year}`;
  } else if (formatId === 'date-us-slash') {
    return `${month}/${day}/${year}`;
  } else if (formatId === 'date-us-dash') {
    return `${month}-${day}-${year}`;
  }

  // Default fallback
  return `${day}/${month}/${year}`;
}

/** Get date format placeholder for user guidance */
function getDateFormatPlaceholder(formatId: string): string {
  if (formatId === 'date-iso') return 'YYYY-MM-DD';
  if (formatId === 'date-eu-slash') return 'DD/MM/YYYY';
  if (formatId === 'date-eu-dash') return 'DD-MM-YYYY';
  if (formatId === 'date-eu-dot') return 'DD.MM.YYYY';
  if (formatId === 'date-us-slash') return 'MM/DD/YYYY';
  if (formatId === 'date-us-dash') return 'MM-DD-YYYY';
  return 'DD/MM/YYYY';
}

/** Get icon for entity based on formatId and outputFormat type - matches model editor logic */
function getEntityIcon(entity: Entity): Icons {
  const formatId = entity.outputFormat?.formatId;
  const outputType = entity.outputFormat?.type;
  const regex = entity.outputFormat?.type === 'string' ? entity.outputFormat.regex : undefined;

  // Custom regex format - use Regex icon
  // Check if formatId is 'custom' OR if there's a regex that doesn't match any known format
  if (formatId === 'custom') {
    return 'Regex';
  }

  // Check if string type has regex but no matching formatId (likely custom)
  if (outputType === 'string' && regex && !formatId) {
    // Check if regex matches any known format
    const allOptions = getRegexOptions();
    let isKnownFormat = false;
    for (const options of Object.values(allOptions)) {
      if (options.some((opt) => opt.regex === regex)) {
        isKnownFormat = true;
        break;
      }
    }
    // If regex exists but doesn't match known formats, it's custom
    if (!isKnownFormat) {
      return 'Regex';
    }
  }

  // Date formats (category: Date)
  if (formatId && typeof formatId === 'string' && formatId.startsWith('date-')) {
    return 'Calendar';
  }

  // Number formats (category: Number)
  if (outputType === 'number' || outputType === 'integer') {
    return 'Hash';
  }

  // Boolean format (category: Boolean)
  if (outputType === 'boolean') {
    return 'ToggleRight';
  }

  // String formats with formatId - check specific format types
  if (formatId && typeof formatId === 'string') {
    if (formatId === 'text-email') {
      return 'Mail';
    }
    if (formatId === 'text-phone') {
      return 'Phone';
    }
    if (formatId === 'text-url') {
      return 'Link';
    }
    // Currency formats
    if (formatId.startsWith('currency-') || formatId.startsWith('number-percentage')) {
      return 'DollarSign';
    }
  }

  // Default to text icon (category: Text)
  return 'TextAlignStart';
}

/** Get children entities for a given parent */
function getChildEntities(allEntities: Entity[], parentId: string | null): Entity[] {
  return allEntities.filter((e) => e.parentEntityId === parentId);
}

/** Determine entity type based on outputFormat and children */
function getEntityType(entity: Entity, allEntities: Entity[]): EntityType {
  const children = getChildEntities(allEntities, entity.id);
  if (children.length === 0) return 'PRIMITIVE';
  return entity.outputFormat?.isArray ? 'ARRAY' : 'OBJECT';
}

/** Get primitive (leaf) children of a parent */
function getPrimitiveChildren(allEntities: Entity[], parentId: string | null): Entity[] {
  return allEntities.filter((e) => {
    if (e.parentEntityId !== parentId) return false;
    return getChildEntities(allEntities, e.id).length === 0;
  });
}

/** Get nested (non-primitive) children of a parent */
function getNestedChildren(allEntities: Entity[], parentId: string | null): Entity[] {
  return allEntities.filter((e) => {
    if (e.parentEntityId !== parentId) return false;
    return getChildEntities(allEntities, e.id).length > 0;
  });
}

/** Check if entity value is empty */
function isValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  // Explicitly check for boolean false - it's a valid value, not empty
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value) && value.length === 0) return true;
  if (Array.isArray(value) && value.every((v) => v === null)) return true;
  return false;
}

/** Get value by entity key or ID (for backwards compatibility) */
function getEntityValue(values: Entities[string] | undefined, entity: Entity): EntityValue | undefined {
  if (!values || typeof values !== 'object') return undefined;
  return values[entity.key] ?? values[entity.id];
}

/** Get the actual key used in the values object */
function getEntityKeyInValues(values: Entities[string] | undefined, entity: Entity): string {
  if (!values || typeof values !== 'object') return entity.key;
  if (entity.key in values) return entity.key;
  if (entity.id in values) return entity.id;
  return entity.key;
}

// ============================================================================
// Number Input Component (for number and integer types)
// ============================================================================

function NumberInput({ entity, value, path, editable, onSave }: PrimitiveValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.filter((v) => v !== null).join(', ');
    return String(value);
  }, [value]);

  const handleStartEdit = () => {
    if (!editable) return;
    setInputValue(displayValue);
    setValidationError(null);
    setIsEditing(true);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    const validation = validateNumberInput(newValue, entity);
    setValidationError(validation.error);
  };

  const handleSave = () => {
    const trimmedValue = inputValue.trim();

    if (validationError) {
      setIsEditing(false);
      setInputValue(displayValue);
      setValidationError(null);
      return;
    }

    if (!trimmedValue) {
      onSave?.(entity.key, null, path);
      setIsEditing(false);
      setInputValue('');
      setValidationError(null);
      return;
    }

    const validation = validateNumberInput(trimmedValue, entity);
    if (validation.isValid && validation.parsed !== undefined) {
      onSave?.(entity.key, validation.parsed, path);
      setIsEditing(false);
      setInputValue('');
      setValidationError(null);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue(displayValue);
    setValidationError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !validationError) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (validationError) {
        handleCancel();
      } else {
        handleSave();
      }
    }, 100);
  };

  return (
    <div
      data-entity-path={path.join('.')}
      onClick={editable && !isEditing ? handleStartEdit : undefined}
      className={cn(
        'pl-1 min-h-7 max-w-full shrink-0 flex gap-1 relative items-center rounded-sm w-full p-0.5 leading-tight group/entity-value',
        editable && 'cursor-pointer hover:bg-accent',
        isEditing && 'bg-accent'
      )}
    >
      {isEditing ? (
        <div className="flex-1 flex flex-col">
          <input
            type="number"
            step={entity.outputFormat?.type === 'integer' ? '1' : 'any'}
            autoFocus
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder="Enter number"
            className={cn(
              'w-full min-h-6 px-1 bg-neutral-100 rounded-sm text-sm outline-none focus:bg-white focus:border',
              validationError ? 'border-destructive focus:border-destructive' : 'focus:border-primary'
            )}
          />
          {validationError && (
            <span className="absolute top-full left-0 text-xs text-destructive mt-0.5 whitespace-nowrap z-10">
              {validationError}
            </span>
          )}
        </div>
      ) : displayValue ? (
        <div className="relative min-h-6 inline-flex rounded-sm items-center bg-white px-1 flex-1 min-w-0">
          <span className="leading-5 break-all">{displayValue}</span>
        </div>
      ) : (
        <span className="text-typography-darkgrey ml-1 flex-1 min-w-0">
          {editable ? (
            <>
              <span className="group-hover/entity-value:hidden">Empty</span>
              <span className="hidden group-hover/entity-value:inline">Add a value</span>
            </>
          ) : (
            'Empty'
          )}
        </span>
      )}

      <DynamicIcon name={getEntityIcon(entity)} size={14} className="shrink-0 text-typography-grey" />
    </div>
  );
}

// ============================================================================
// Boolean Input Component
// ============================================================================

function BooleanInput({ entity, value, path, editable, onSave }: PrimitiveValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      if (lower === 'true' || lower === '1') return true;
      if (lower === 'false' || lower === '0') return false;
    }
    return null;
  }, [value]);

  // Click outside detection
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };

    // Use mousedown to detect clicks before they complete
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing]);

  const handleStartEdit = () => {
    if (!editable) return;
    setIsEditing(true);
  };

  const handleSelect = (newValue: boolean | null) => {
    onSave?.(entity.key, newValue, path);
    setIsEditing(false);
  };

  return (
    <div
      ref={containerRef}
      data-entity-path={path.join('.')}
      onClick={editable && !isEditing ? handleStartEdit : undefined}
      className={cn(
        'pl-1 min-h-7 max-w-full shrink-0 flex gap-1 relative items-center rounded-sm w-full p-0.5 leading-tight group/entity-value',
        editable && 'cursor-pointer hover:bg-accent',
        isEditing && 'bg-accent'
      )}
    >
      {isEditing ? (
        <div className="flex items-center gap-1 flex-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(true);
            }}
            className={cn(
              'min-h-6 px-2 rounded-sm text-sm border transition-colors',
              displayValue === true
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-typography-black border-border hover:bg-accent'
            )}
          >
            true
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(false);
            }}
            className={cn(
              'min-h-6 px-2 rounded-sm text-sm border transition-colors',
              displayValue === false
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-typography-black border-border hover:bg-accent'
            )}
          >
            false
          </button>
          {displayValue !== null && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(null);
              }}
              className="min-h-6 px-2 rounded-sm text-sm border border-border bg-white text-typography-darkgrey hover:bg-accent hover:text-typography-black"
            >
              Clear
            </button>
          )}
        </div>
      ) : displayValue === true ? (
        <div className="relative min-h-6 inline-flex rounded-sm items-center bg-white px-1">
          <span className="leading-5">true</span>
        </div>
      ) : displayValue === false ? (
        <div className="relative min-h-6 inline-flex rounded-sm items-center bg-white px-1">
          <span className="leading-5">false</span>
        </div>
      ) : (
        <span className="text-typography-darkgrey ml-1">
          {editable ? (
            <>
              <span className="group-hover/entity-value:hidden">Empty</span>
              <span className="hidden group-hover/entity-value:inline">Click to set value</span>
            </>
          ) : (
            'Empty'
          )}
        </span>
      )}

      <DynamicIcon name={getEntityIcon(entity)} size={14} className="ml-auto shrink-0 text-typography-grey" />
    </div>
  );
}

// ============================================================================
// Date Input Component (for date formats)
// ============================================================================

function DateInput({ entity, value, path, editable, onSave }: PrimitiveValueProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const formatId = entity.outputFormat?.formatId || '';

  const displayValue = useMemo(() => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;
    return String(value);
  }, [value]);

  const selectedDate = useMemo(() => {
    if (!displayValue) return undefined;
    return parseDateString(displayValue, formatId);
  }, [displayValue, formatId]);

  // Sync raw input with display value when popover opens
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setRawInput(displayValue);
      setValidationError(null);
    }
    setIsOpen(open);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    const formattedDate = formatDateString(date, formatId);
    setRawInput(formattedDate);
    setValidationError(null);
    onSave?.(entity.key, formattedDate, path);
    setIsOpen(false);
  };

  const handleRawInputChange = (inputValue: string) => {
    setRawInput(inputValue);
    // Use the same regex validation as other string inputs
    const validation = validateStringInput(inputValue, entity);
    setValidationError(validation.error);
  };

  const handleRawInputSave = () => {
    const trimmed = rawInput.trim();
    if (!trimmed) {
      onSave?.(entity.key, null, path);
      setIsOpen(false);
      return;
    }

    // Use the same regex validation as other string inputs
    const validation = validateStringInput(trimmed, entity);
    if (validation.isValid) {
      onSave?.(entity.key, trimmed, path);
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setRawInput('');
    setValidationError(null);
    onSave?.(entity.key, null, path);
    setIsOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !validationError) {
      handleRawInputSave();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div
      data-entity-path={path.join('.')}
      className={cn(
        'pl-1 min-h-7 max-w-full shrink-0 flex gap-1 relative items-center rounded-sm w-full p-0.5 leading-tight group/entity-value',
        editable && 'cursor-pointer hover:bg-accent'
      )}
    >
      {editable ? (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex items-center gap-1 min-h-6 px-1 rounded-sm text-left flex-1 min-w-0',
                displayValue ? 'bg-white' : 'text-typography-darkgrey'
              )}
            >
              {displayValue ? (
                <span className="leading-5 break-all">{displayValue}</span>
              ) : (
                <span>
                  <span className="group-hover/entity-value:hidden">Empty</span>
                  <span className="hidden group-hover/entity-value:inline">Click to select date</span>
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              className="z-50 rounded-sm border bg-white p-0 text-typography-black shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
              align="start"
              side="bottom"
              sideOffset={4}
            >
              <Calendar
                mode="single"
                selected={selectedDate ?? undefined}
                defaultMonth={selectedDate ?? undefined}
                onSelect={handleDateSelect}
                initialFocus
              />
              <div className="border-t px-3 py-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={rawInput}
                      onChange={(e) => handleRawInputChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={getDateFormatPlaceholder(formatId)}
                      className={cn(
                        'w-full h-8 px-2 text-sm bg-neutral-50 rounded-sm border outline-none focus:bg-white',
                        validationError
                          ? 'border-destructive focus:border-destructive'
                          : 'border-neutral-200 focus:border-primary'
                      )}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="h-8 px-2 text-sm text-typography-grey hover:text-destructive hover:bg-destructive/10 rounded-sm transition-colors"
                  >
                    Clear
                  </button>
                </div>
                {validationError && <p className="text-xs text-destructive">{validationError}</p>}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </Popover>
      ) : displayValue ? (
        <div className="relative min-h-6 inline-flex rounded-sm items-center bg-white px-1 flex-1 min-w-0">
          <span className="leading-5 break-all">{displayValue}</span>
        </div>
      ) : (
        <span className="text-typography-darkgrey ml-1 flex-1 min-w-0">Empty</span>
      )}

      <DynamicIcon name={getEntityIcon(entity)} size={14} className="shrink-0 text-typography-grey" />
    </div>
  );
}

// ============================================================================
// Single Value Input Component (isArray: false) - for strings only
// ============================================================================

function SingleValueInput({ entity, value, path, editable, onSave }: PrimitiveValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.filter((v) => v !== null).join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }, [value]);

  const handleStartEdit = () => {
    if (!editable) return;
    setInputValue(displayValue);
    setValidationError(null);
    setIsEditing(true);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    const validation = validateStringInput(newValue, entity);
    setValidationError(validation.error);
  };

  const handleSave = () => {
    const trimmedValue = inputValue.trim();

    if (validationError) {
      setIsEditing(false);
      setInputValue(displayValue);
      setValidationError(null);
      return;
    }

    onSave?.(entity.key, trimmedValue || null, path);
    setIsEditing(false);
    setValidationError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue(displayValue);
    setValidationError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !validationError) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (validationError) {
        handleCancel();
      } else {
        handleSave();
      }
    }, 100);
  };

  return (
    <div
      data-entity-path={path.join('.')}
      onClick={editable && !isEditing ? handleStartEdit : undefined}
      className={cn(
        'pl-1 min-h-7 max-w-full shrink-0 flex gap-1 relative items-center rounded-sm w-full p-0.5 leading-tight group/entity-value',
        editable && 'cursor-pointer hover:bg-accent',
        isEditing && 'bg-accent'
      )}
    >
      {isEditing ? (
        <div className="flex-1 flex flex-col">
          <input
            type="text"
            autoFocus
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder="Enter value"
            className={cn(
              'w-full min-h-6 px-1 bg-neutral-100 rounded-sm text-sm outline-none focus:bg-white focus:border',
              validationError ? 'border-destructive focus:border-destructive' : 'focus:border-primary'
            )}
          />
          {validationError && (
            <span className="absolute top-full left-0 text-xs text-destructive mt-0.5 whitespace-nowrap z-10">
              {validationError}
            </span>
          )}
        </div>
      ) : displayValue ? (
        <div className="relative min-h-6 inline-flex rounded-sm items-center bg-white px-1 flex-1 min-w-0">
          <span className="leading-5 break-all">{displayValue}</span>
        </div>
      ) : (
        <span className="text-typography-darkgrey ml-1 flex-1 min-w-0">
          {editable ? (
            <>
              <span className="group-hover/entity-value:hidden">Empty</span>
              <span className="hidden group-hover/entity-value:inline">Add a value</span>
            </>
          ) : (
            'Empty'
          )}
        </span>
      )}

      <DynamicIcon name={getEntityIcon(entity)} size={14} className="shrink-0 text-typography-grey" />
    </div>
  );
}

// ============================================================================
// Multi Value Input Component (isArray: true)
// ============================================================================

function MultiValueInput({ entity, value, path, editable, onSave }: PrimitiveValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<(string | number | boolean)[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const originalValues = useMemo(() => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) {
      return value.filter((v) => v !== null) as (string | number | boolean)[];
    }
    return [value] as (string | number | boolean)[];
  }, [value]);

  const displayValues = useMemo(() => {
    return originalValues.map((v) => {
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    });
  }, [originalValues]);

  const handleStartEdit = () => {
    if (!editable) return;
    setEditValues([...originalValues]);
    setInputValue('');
    setValidationError(null);
    setIsEditing(true);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    const outputType = entity.outputFormat?.type;
    let validation: { isValid: boolean; error: string | null; parsed?: number };

    if (outputType === 'string') {
      const stringValidation = validateStringInput(newValue, entity);
      validation = stringValidation;
    } else if (outputType === 'number' || outputType === 'integer') {
      validation = validateNumberInput(newValue, entity);
    } else {
      validation = { isValid: true, error: null };
    }

    setValidationError(validation.error);
  };

  const handleSave = () => {
    const outputType = entity.outputFormat?.type;
    let finalValues: (string | number | boolean)[] = [...editValues];
    const trimmedInput = inputValue.trim();

    if (trimmedInput) {
      if (outputType === 'string') {
        const validation = validateStringInput(trimmedInput, entity);
        if (validation.isValid) {
          finalValues = [...editValues, trimmedInput];
        }
      } else if (outputType === 'number' || outputType === 'integer') {
        const validation = validateNumberInput(trimmedInput, entity);
        if (validation.isValid && validation.parsed !== undefined) {
          finalValues = [...editValues, validation.parsed];
        }
      } else if (outputType === 'boolean') {
        const lower = trimmedInput.toLowerCase();
        if (lower === 'true' || lower === '1') {
          finalValues = [...editValues, true];
        } else if (lower === 'false' || lower === '0') {
          finalValues = [...editValues, false];
        }
      }
    }

    // For arrays, use empty array instead of null when empty
    onSave?.(
      entity.key,
      (finalValues.length > 0 ? finalValues : []) as string | number | boolean | null | string[] | number[] | boolean[],
      path
    );
    setIsEditing(false);
    setInputValue('');
    setEditValues([]);
    setValidationError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValues([]);
    setInputValue('');
    setValidationError(null);
  };

  const handleDeleteValue = (index: number) => {
    setEditValues((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (inputValue.trim()) {
        const outputType = entity.outputFormat?.type;

        if (outputType === 'string') {
          const validation = validateStringInput(inputValue.trim(), entity);
          if (validation.isValid) {
            setEditValues([...editValues, inputValue.trim()]);
            setInputValue('');
            setValidationError(null);
          }
        } else if (outputType === 'number' || outputType === 'integer') {
          const validation = validateNumberInput(inputValue.trim(), entity);
          if (validation.isValid && validation.parsed !== undefined) {
            setEditValues([...editValues, validation.parsed]);
            setInputValue('');
            setValidationError(null);
          }
        } else if (outputType === 'boolean') {
          const lower = inputValue.trim().toLowerCase();
          if (lower === 'true' || lower === '1') {
            setEditValues([...editValues, true]);
            setInputValue('');
            setValidationError(null);
          } else if (lower === 'false' || lower === '0') {
            setEditValues([...editValues, false]);
            setInputValue('');
            setValidationError(null);
          }
        }
      } else {
        handleSave();
      }
    } else if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Backspace' && !inputValue && editValues.length > 0) {
      handleDeleteValue(editValues.length - 1);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (document.activeElement?.closest(`[data-entity-path="${path.join('.')}"]`)) return;
      handleSave();
    }, 150);
  };

  return (
    <div
      data-entity-path={path.join('.')}
      onClick={editable && !isEditing ? handleStartEdit : undefined}
      className={cn(
        'pl-1 min-h-7 max-w-full shrink-0 flex gap-1 relative items-center rounded-sm w-full p-0.5 leading-tight group/entity-value',
        editable && 'cursor-pointer hover:bg-accent',
        isEditing && 'bg-accent'
      )}
    >
      {isEditing ? (
        <div className="flex flex-wrap gap-1 flex-1 items-center">
          {editValues.map((val, index) => (
            <div
              key={index}
              className="relative min-h-6 inline-flex rounded-sm items-stretch bg-white group/value-item"
            >
              <div className="leading-5 flex-center break-all min-h-6 px-1 rounded-l-sm">
                {typeof val === 'object' ? JSON.stringify(val) : String(val)}
              </div>
              <WithTooltip tooltip="Delete">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteValue(index);
                  }}
                  className="flex-center w-5 shrink-0 rounded-r-sm h-full text-typography-darkgrey hover:text-destructive hover:bg-accent"
                >
                  <X size={14} />
                </button>
              </WithTooltip>
            </div>
          ))}
          <div className="flex-1 min-w-20 flex flex-col relative">
            <input
              type={
                entity.outputFormat?.type === 'number' || entity.outputFormat?.type === 'integer' ? 'number' : 'text'
              }
              step={entity.outputFormat?.type === 'integer' ? '1' : 'any'}
              autoFocus
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={editValues.length > 0 ? 'Add value' : 'Enter value'}
              className={cn(
                'w-full min-h-6 px-1 bg-neutral-100 rounded-sm text-sm outline-none focus:bg-white focus:border',
                validationError ? 'border-destructive focus:border-destructive' : 'focus:border-primary'
              )}
            />
            {validationError && (
              <span className="absolute top-full left-0 text-xs text-destructive mt-0.5 whitespace-nowrap z-10">
                {validationError}
              </span>
            )}
          </div>
        </div>
      ) : displayValues.length > 0 ? (
        <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
          {displayValues.map((val, index) => (
            <div key={index} className="relative min-h-6 inline-flex rounded-sm items-center bg-white px-1">
              <span className="leading-5 break-all">{val}</span>
            </div>
          ))}
        </div>
      ) : (
        <span className="text-typography-darkgrey ml-1 flex-1 min-w-0">
          {editable ? (
            <>
              <span className="group-hover/entity-value:hidden">Empty</span>
              <span className="hidden group-hover/entity-value:inline">Add a value</span>
            </>
          ) : (
            'Empty'
          )}
        </span>
      )}

      <DynamicIcon name={getEntityIcon(entity)} size={14} className="shrink-0 text-typography-grey" />
    </div>
  );
}

// ============================================================================
// Primitive Value (delegates to appropriate input based on entity type)
// ============================================================================

function PrimitiveValue({ entity, value, path, editable, onSave }: PrimitiveValueProps) {
  const outputType = entity.outputFormat?.type;
  const isArray = entity.outputFormat?.isArray;

  if (isArray) {
    return <MultiValueInput entity={entity} value={value} path={path} editable={editable} onSave={onSave} />;
  }

  if (outputType === 'number' || outputType === 'integer') {
    return <NumberInput entity={entity} value={value} path={path} editable={editable} onSave={onSave} />;
  }

  if (outputType === 'boolean') {
    return <BooleanInput entity={entity} value={value} path={path} editable={editable} onSave={onSave} />;
  }

  // Check if it's a date format (string type with date formatId)
  if (outputType === 'string' && isDateFormat(entity)) {
    return <DateInput entity={entity} value={value} path={path} editable={editable} onSave={onSave} />;
  }

  // Default to string input
  return <SingleValueInput entity={entity} value={value} path={path} editable={editable} onSave={onSave} />;
}

// ============================================================================
// Primitive Entity List (renders leaf entities)
// ============================================================================

type PrimitiveEntityListProps = {
  entities: Entity[];
  values: Entities[string];
  path: string[];
  showEmptyTags: boolean;
  editable?: boolean;
  onSave?: (
    key: string,
    value: string | number | boolean | string[] | number[] | boolean[] | null,
    path: string[]
  ) => void;
};

function PrimitiveEntityList({ entities, values, path, showEmptyTags, editable, onSave }: PrimitiveEntityListProps) {
  const filteredEntities = useMemo(() => {
    if (showEmptyTags) return entities;
    return entities.filter((entity) => !isValueEmpty(getEntityValue(values, entity)));
  }, [entities, values, showEmptyTags]);

  return (
    <div className="flex flex-col">
      {filteredEntities
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((entity) => {
          const value = getEntityValue(values, entity);
          const valueKey = getEntityKeyInValues(values, entity);
          return (
            <div key={entity.id} className="min-h-7 group/primitive-entity-value flex w-full flex-row items-center">
              <div className="flex items-center shrink-0">
                <TagNameBadge
                  isPrimitive
                  size="sm"
                  tagName={entity.name}
                  className="group-hover/primitive-entity-value:bg-blue-100"
                />
                <ArrowRight size={16} className="text-typography-grey shrink-0" />
              </div>
              <div className="flex items-center flex-1 min-w-0 pl-0">
                <div className="flex-1 min-w-0 pl-1 py-1">
                  <PrimitiveValue
                    entity={entity}
                    value={value as string | number | boolean | string[] | number[] | boolean[] | null}
                    path={[...path, valueKey]}
                    editable={editable}
                    onSave={onSave}
                  />
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}

// ============================================================================
// Internal Tree Component (recursive)
// ============================================================================

function EntityTreeInternal({
  model,
  values,
  depth = 0,
  path = [],
  parentEntity,
  showMissingTags: _showMissingTags = true,
  editable,
  onSave,
}: InternalTreeProps) {
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([]);
  const [hiddenEmptyTagsKeys, setHiddenEmptyTagsKeys] = useState<string[]>([]);
  const [showEmptyTags, setShowEmptyTags] = useState(true);

  const parentId = parentEntity?.id || null;
  const primitiveEntities = getPrimitiveChildren(model.entities, parentId);
  const nestedEntities = getNestedChildren(model.entities, parentId);

  const emptyPrimitiveTags = useMemo(() => {
    return primitiveEntities.filter((entity) => isValueEmpty(getEntityValue(values, entity)));
  }, [primitiveEntities, values]);

  const toggleCollapse = (key: string) => {
    setCollapsedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleHideEmpty = (key: string) => {
    setHiddenEmptyTagsKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  if (primitiveEntities.length === 0 && nestedEntities.length === 0) {
    return <div className="text-sm text-gray-500 italic p-2">No entities defined</div>;
  }

  return (
    <div className="flex flex-col gap-1">
      {/* Primitive entities section */}
      {primitiveEntities.length > 0 && (
        <div className={cn('bg-white rounded-sm', depth === 0 && 'border px-1 pt-[3px] pb-[3px]')}>
          {emptyPrimitiveTags.length > 0 && depth === 0 && (
            <div className="flex items-center gap-1 opacity-50 hover:opacity-100 px-1">
              <button
                className="flex shrink-0 py-0.5 text-xs items-center gap-1 text-typography-grey hover:text-black"
                onClick={() => setShowEmptyTags((prev) => !prev)}
              >
                {showEmptyTags ? <EyeOff size={14} /> : <Eye size={14} />}
                {showEmptyTags ? 'Hide' : 'Show'} {emptyPrimitiveTags.length} empty{' '}
                {pluralize('tag', emptyPrimitiveTags.length)}
              </button>
            </div>
          )}
          <div className="min-h-0 pt-0.5 px-1">
            <PrimitiveEntityList
              entities={primitiveEntities}
              values={values}
              path={path}
              showEmptyTags={depth === 0 ? showEmptyTags : !hiddenEmptyTagsKeys.includes(parentEntity?.key || '')}
              editable={editable}
              onSave={onSave}
            />
          </div>
        </div>
      )}

      {/* Nested entities (arrays and objects) */}
      {nestedEntities.map((entity) => {
        const entityType = getEntityType(entity, model.entities);
        const isCollapsed = collapsedKeys.includes(entity.key);
        const isHiddenEmpty = hiddenEmptyTagsKeys.includes(entity.key);
        const nestedValue = getEntityValue(values, entity);
        const valueKey = getEntityKeyInValues(values, entity);
        const arrayCount = Array.isArray(nestedValue)
          ? nestedValue.filter((item) => item !== null && item !== undefined).length
          : 0;

        return (
          <div key={entity.id} className="border bg-white rounded-sm p-1">
            <div className="flex items-center justify-between gap-1">
              <TagNameBadge
                isPrimitive={false}
                className="max-w-full w-full cursor-text hover:bg-white"
                tagName={entity.name + (entityType === 'ARRAY' ? ` (${arrayCount})` : '')}
              >
                <div className="flex items-center gap-1">
                  <button
                    className="shrink-0 select-none opacity-50 hover:opacity-100 text-xs flex-center px-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHideEmpty(entity.key);
                    }}
                  >
                    {isHiddenEmpty ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                  <button
                    className="shrink-0 select-none opacity-50 hover:opacity-100 text-xs flex-center gap-1 px-1"
                    onClick={() => toggleCollapse(entity.key)}
                  >
                    {isCollapsed ? <ChevronsUpDown size={12} /> : <ChevronsDownUp size={12} />}
                  </button>
                  <WithAccessControl>
                    <CopyButton className="px-1" variant="subtle" value={JSON.stringify(nestedValue || {}, null, 4)} />
                  </WithAccessControl>
                </div>
              </TagNameBadge>
            </div>

            {!isCollapsed && (
              <>
                {entityType === 'ARRAY' ? (
                  <div className="flex flex-col gap-1 max-w-full mt-1">
                    {Array.isArray(nestedValue) &&
                      nestedValue.map((item, index) => {
                        if (!isEntityObject(item)) return null;
                        return (
                          <div
                            key={index}
                            className="border p-1 rounded-sm hover:border-neutral-500 relative group/array-item"
                          >
                            {editable && (
                              <WithTooltip tooltip="Delete item">
                                <button
                                  onClick={() => {
                                    // Remove item at index from array
                                    const currentArray = Array.isArray(nestedValue)
                                      ? nestedValue.filter(isEntityObject)
                                      : [];
                                    const updatedArray = currentArray.filter((_, i) => i !== index);

                                    // Call onSave with the updated array (empty array, not null, for arrays)
                                    onSave?.(valueKey, updatedArray, [...path, valueKey]);
                                  }}
                                  className="absolute top-1 right-1 opacity-0 group-hover/array-item:opacity-100 transition-opacity flex-center w-5 h-5 rounded-sm text-typography-darkgrey hover:text-destructive hover:bg-accent z-10"
                                >
                                  <X size={14} />
                                </button>
                              </WithTooltip>
                            )}
                            <EntityTreeInternal
                              model={model}
                              values={item}
                              depth={depth + 1}
                              path={[...path, valueKey, index.toString()]}
                              parentEntity={entity}
                              showMissingTags={!isHiddenEmpty}
                              editable={editable}
                              onSave={onSave}
                            />
                          </div>
                        );
                      })}
                    {editable && (
                      <button
                        onClick={() => {
                          // Create empty object with all child entities initialized to null
                          function createEmptyObject(parentEntityId: string | null): Entities[string] {
                            const obj: Entities[string] = {};
                            const children = getChildEntities(model.entities, parentEntityId);

                            for (const child of children) {
                              const childChildren = getChildEntities(model.entities, child.id);
                              if (childChildren.length > 0) {
                                // If child has nested children, create nested object
                                obj[child.key] = createEmptyObject(child.id);
                              } else {
                                // For leaf nodes, set null value
                                obj[child.key] = null;
                              }
                            }

                            return obj;
                          }

                          const newObject = createEmptyObject(entity.id);

                          // Get current array value or initialize as empty array
                          const currentArray = Array.isArray(nestedValue) ? nestedValue.filter(isEntityObject) : [];

                          // Add new object to array
                          const updatedArray = [...currentArray, newObject];

                          // Call onSave with the updated array
                          // Use valueKey (the actual key in values) and include it in the path
                          onSave?.(valueKey, updatedArray, [...path, valueKey]);
                        }}
                        className="hover:bg-primary hover:text-white hover:border-solid hover:border-primary transition-all text-primary-500 px-2 py-1 rounded-sm border border-dashed flex items-center gap-1 text-sm text-typography-grey"
                      >
                        <Plus size={16} /> Add {entity.name}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 mt-1">
                    <EntityTreeInternal
                      model={model}
                      values={isEntityObject(nestedValue) ? nestedValue : {}}
                      depth={depth + 1}
                      path={[...path, valueKey]}
                      parentEntity={entity}
                      showMissingTags={!isHiddenEmpty}
                      editable={editable}
                      onSave={onSave}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main Export: NextGenEntityEditor
// ============================================================================

/**
 * A dumb component for viewing and editing next-gen entity predictions.
 *
 * @param model - The extraction model definition containing entity schemas
 * @param values - The prediction values (JSON object)
 * @param editable - Whether editing is enabled (default: false)
 * @param onSave - Callback when a value is saved
 *
 * @example
 * ```tsx
 * <NextGenEntityEditor
 *   model={extractionModel}
 *   values={prediction}
 *   editable={true}
 *   onSave={(key, value, path) => {
 *     console.log('Saved:', { key, value, path });
 *   }}
 * />
 * ```
 */
export default function NextGenEntityEditor({ model, values, editable = false, onSave }: NextGenEntityEditorProps) {
  return <EntityTreeInternal model={model} values={values} editable={editable} onSave={onSave} />;
}

// Also export as NextGenEntityTree for backwards compatibility
export { NextGenEntityEditor as NextGenEntityTree };

// ============================================================================
// Utility: Generate Empty Skeleton from Model
// ============================================================================

/**
 * Creates an empty skeleton object from a model's entity definitions.
 * All primitive fields are set to null, array fields are set to empty arrays.
 * This ensures the structure is complete for validation and storage.
 */
export function generateEmptySkeleton(model: ExtractionModel): Entities[string] {
  const result: Entities[string] = {};

  // Get top-level entities (no parent)
  const topLevelEntities = model.entities.filter((e) => !e.parentEntityId);

  for (const entity of topLevelEntities) {
    const children = getChildEntities(model.entities, entity.id);

    if (children.length === 0) {
      // Leaf node - set to null
      result[entity.key] = null;
    } else if (entity.outputFormat?.isArray) {
      // Array entity - set to empty array
      result[entity.key] = [];
    } else {
      // Object entity - recurse
      result[entity.key] = generateNestedSkeleton(model.entities, entity.id);
    }
  }

  return result;
}

/**
 * Recursively generates nested skeleton for a parent entity
 */
function generateNestedSkeleton(allEntities: Entity[], parentId: string): Entities[string] {
  const result: Entities[string] = {};
  const children = getChildEntities(allEntities, parentId);

  for (const child of children) {
    const childChildren = getChildEntities(allEntities, child.id);

    if (childChildren.length === 0) {
      // Leaf node - set to null
      result[child.key] = null;
    } else if (child.outputFormat?.isArray) {
      // Array entity - set to empty array
      result[child.key] = [];
    } else {
      // Object entity - recurse
      result[child.key] = generateNestedSkeleton(allEntities, child.id);
    }
  }

  return result;
}

/**
 * Merges existing values with the skeleton, ensuring all fields are present.
 * Values from `values` take precedence over skeleton defaults.
 */
export function mergeWithSkeleton(skeleton: Entities[string], values: Entities[string] | undefined): Entities[string] {
  if (!values || Object.keys(values).length === 0) {
    return skeleton;
  }

  const result: Entities[string] = { ...skeleton };

  for (const key of Object.keys(values)) {
    const skeletonValue = skeleton[key];
    const currentValue = values[key];

    if (currentValue !== undefined) {
      if (
        typeof skeletonValue === 'object' &&
        skeletonValue !== null &&
        !Array.isArray(skeletonValue) &&
        typeof currentValue === 'object' &&
        currentValue !== null &&
        !Array.isArray(currentValue)
      ) {
        // Both are objects - recurse
        result[key] = mergeWithSkeleton(skeletonValue as Entities[string], currentValue as Entities[string]);
      } else {
        // Use the provided value
        result[key] = currentValue;
      }
    }
  }

  return result;
}
